import { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

const FormatContext = createContext();

// 预设币种配置
const DEFAULT_CURRENCY_CONFIG = {
  USD: { symbol: '$', accounting: true, decimalDigits: 2 },
  EUR: { symbol: '€', accounting: true, decimalDigits: 2 },
  GBP: { symbol: '£', accounting: true, decimalDigits: 2 },
  JPY: { symbol: '¥', accounting: false, decimalDigits: 0 },
  INR: { symbol: '₹', accounting: true, decimalDigits: 2 },
  AUD: { symbol: 'A$', accounting: true, decimalDigits: 2 },
  ZAR: { symbol: 'R', accounting: true, decimalDigits: 2 }
};

const createFormatters = (locale, config = {}) => {
  // 创建基础格式化器
  const baseFormatters = {
    date: {
      short: new Intl.DateTimeFormat(locale, { dateStyle: 'short' }),
      medium: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }),
      numeric: new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }),
      monthday: new Intl.DateTimeFormat(locale, {
        month: '2-digit',
        day: '2-digit'
      })
    },
    datetime: new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }),  // 添加 datetime 格式化器，包含日期和时间
    number: {
      standard: new Intl.NumberFormat(locale, {
        useGrouping: true,
        minimumFractionDigits: 2
      }),
      plain: new Intl.NumberFormat(locale, {
        useGrouping: false
      })
    }
  };

  // 创建自定义格式化器
  const customFormatters = {
    date: {
      short: new Intl.DateTimeFormat(locale, {
        dateStyle: 'short',
        ...config.date?.short
      }),
      // 其他日期格式...
    },
    number: {
      // 自定义数字格式...
    },
    // 新增货币格式化器
    currency: Object.keys(DEFAULT_CURRENCY_CONFIG).reduce((acc, currency) => {
      const currencyConfig = {
        ...DEFAULT_CURRENCY_CONFIG[currency],
        ...config.currency?.[currency]
      };
      acc[currency] = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: currencyConfig.decimalDigits,
        maximumFractionDigits: currencyConfig.decimalDigits,
        currencySign: 'standard'  // 永远使用负号格式 -100.00
      });
      return acc;
    }, {})
  };
  return { baseFormatters, customFormatters };
};

export function FormatProvider({ children, config = {} }) {
  const locale = useSelector((state) => state.language.lang);

  const { baseFormatters, customFormatters } = useMemo(() => {
    return createFormatters(locale, config);
  }, [locale, config]);

  const createCurrencyFormat = (currency, options) => {
    const decimals = DEFAULT_CURRENCY_CONFIG[currency]?.decimalDigits ?? 2;

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      currencySign: 'standard',  // 始终负号格式
      ...options
    });
  };

  const value = {
    locale,
    // 基础格式化器
    baseFormatters,
    // 自定义格式化器
    customFormatters,
    // 格式创建方法
    createFormat: (type, options) => {
      return type === 'date'
        ? new Intl.DateTimeFormat(locale, options)
        : new Intl.NumberFormat(locale, options);
    },
    // 货币专用方法
    createCurrencyFormat,
    formatCurrency: (amount, currency) => {
      const formatter = customFormatters.currency[currency] ||
        createCurrencyFormat(currency);
      return formatter.format(amount);
    }
  };

  return (
    <FormatContext.Provider value={value}>
      {children}
    </FormatContext.Provider>
  );
}

export function useFormat() {
  return useContext(FormatContext);
}